import { Typography } from '@mui/joy';
import { Control, Controller, FieldError, UseFormGetValues } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import ReactQuill from 'react-quill';

import 'react-quill/dist/quill.snow.css';
import { FormLabel } from 'core/elements';
import { getPlainTextLength, hasContent } from 'core/utils';
import { toFieldFocusRef } from 'core/utils/fieldFocus';
import { Color } from 'theme/enums';

import { TextEditorWrapper } from './style';

interface IProps {
  name: string;
  placeholder?: string;
  onChange?: (value: any) => void;
  onKeyDown?: (event) => void;
  control?: Control<any>;
  getValues?: UseFormGetValues<any>;
  error?: FieldError | string;
  label?: string;
  tooltip?: string;
  height?: number;
  disabled?: boolean;
  maxLength?: number;
}

const modules = {
  toolbar: [
    [{ header: [1, 2, false] }],
    ['bold', 'italic', 'underline'],
    ['link'],
    [{ color: [] }, { background: [] }],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['clean'],
  ],
};

const formats = [
  'header',
  'bold',
  'italic',
  'underline',
  'list',
  'bullet',
  'link',
  'color',
  'background',
  'clean',
];

const TextEditorView = ({
  name,
  placeholder,
  onChange,
  onKeyDown,
  control,
  error,
  label,
  tooltip,
  height = 240,
  disabled = false,
  maxLength,
}: IProps) => {
  const { t } = useTranslation();

  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => {
        const plainTextLength = getPlainTextLength(field?.value);
        const isOverLimit = !!maxLength && plainTextLength > maxLength;

        return (
          <TextEditorWrapper
            name={name}
            error={!!error || isOverLimit}
            height={height}
            disabled={disabled}
          >
            {label && <FormLabel tooltip={tooltip}>{label}</FormLabel>}
            <ReactQuill
              ref={toFieldFocusRef(field.ref, (quill: ReactQuill) => quill.editor?.root)}
              {...{
                id: name,
                name,
                theme: 'snow',
                value: field?.value,
                placeholder,
                onChange: (value) => {
                  value = hasContent(value) ? value : '';
                  if (onChange) {
                    onChange(value);
                  }
                  field?.onChange(value);
                },
                onKeyDown,
                modules,
                formats,
                error,
                readOnly: disabled,
              }}
            />
            {maxLength && !isOverLimit && (
              <Typography color={Color.NEUTRAL} style={{ marginTop: 4, textAlign: 'right' }}>
                {plainTextLength}/{maxLength}
              </Typography>
            )}
            {isOverLimit && (
              <Typography color={Color.DANGER} style={{ marginTop: 4 }}>
                {t('warningCharacterLimitExceeded', { count: plainTextLength, max: maxLength })}
              </Typography>
            )}
          </TextEditorWrapper>
        );
      }}
    />
  );
};

export default TextEditorView;
